'use client';

import { useState, useEffect } from 'react';
import { notificationsApi } from '@/lib/api/notifications';
import type { Notification, NotificationType } from '@/lib/types/notification.types';
import type { QueryParams } from '@/lib/types/common.types';
import { useAuth } from '@/contexts/AuthContext';
import { useNotificationWebSocket } from '@/contexts/NotificationWebSocketContext';
import type { NotificationMessage } from '@/lib/websocket/notificationClient';
import { useNotificationsInvalidationStore } from '@/store/notificationsInvalidationStore';

export function useNotifications(params?: QueryParams) {
  const [data, setData] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { onNotification } = useNotificationWebSocket();
  const version = useNotificationsInvalidationStore((s) => s.version);
  const paramsKey = JSON.stringify(params ?? {});

  useEffect(() => {
    const fetchNotifications = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await notificationsApi.getAll(params);
        setData(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotifications();
  }, [paramsKey, version]);

  // Listen for real-time notifications and prepend them
  useEffect(() => {
    const unsubscribe = onNotification((message: NotificationMessage) => {
      if (message.type !== 'notification') return;
      const mapped = mapSocketNotification(message);
      setData(prev => [mapped, ...prev]);
    });
    return unsubscribe;
  }, [onNotification]);

  return { data, isLoading, error };
}

export function useNotification(id: string) {
  const [data, setData] = useState<Notification | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const version = useNotificationsInvalidationStore((s) => s.version);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    const fetchNotification = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await notificationsApi.getById(id);
        setData(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotification();
  }, [id, version]);

  return { data, isLoading, error };
}

export function useUnreadNotifications(params?: QueryParams) {
  const [data, setData] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { isAuthenticated } = useAuth();
  const { onNotification } = useNotificationWebSocket();
  const version = useNotificationsInvalidationStore((s) => s.version);
  const paramsKey = JSON.stringify(params ?? {});

  useEffect(() => {
    // Only fetch if user is authenticated
    if (!isAuthenticated) {
      setData([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    const fetchUnread = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await notificationsApi.getUnread(params);
        setData(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUnread();
  }, [paramsKey, isAuthenticated, version]);

  // Real-time unread updates
  useEffect(() => {
    if (!isAuthenticated) return;
    const unsubscribe = onNotification((message: NotificationMessage) => {
      if (message.type !== 'notification') return;
      const mapped = mapSocketNotification(message);
      if (!mapped.isRead) {
        setData(prev => [mapped, ...prev]);
      }
    });
    return unsubscribe;
  }, [isAuthenticated, onNotification]);

  return { data, isLoading, error };
}

export function useUnreadCount() {
  const [data, setData] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { isAuthenticated } = useAuth();
  const { onNotification } = useNotificationWebSocket();
  const version = useNotificationsInvalidationStore((s) => s.version);

  useEffect(() => {
    // Only fetch if user is authenticated
    if (!isAuthenticated) {
      setData(0);
      setIsLoading(false);
      setError(null);
      return;
    }

    const fetchUnreadCount = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await notificationsApi.getUnreadCount();
        setData(result.count);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUnreadCount();
  }, [isAuthenticated, version]);

  // Real-time counter updates
  useEffect(() => {
    if (!isAuthenticated) return;
    const unsubscribe = onNotification((message: NotificationMessage) => {
      if (message.type !== 'notification') return;
      if (message.is_read) return;
      setData(prev => prev + 1);
    });
    return unsubscribe;
  }, [isAuthenticated, onNotification]);

  return { data, isLoading, error };
}

export function useNotificationsByType(type: NotificationType, params?: QueryParams) {
  const [data, setData] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { onNotification } = useNotificationWebSocket();
  const version = useNotificationsInvalidationStore((s) => s.version);
  const paramsKey = JSON.stringify(params ?? {});

  useEffect(() => {
    const fetchByType = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await notificationsApi.getByType(type, params);
        setData(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchByType();
  }, [type, paramsKey, version]);

  // Real-time updates filtered by type
  useEffect(() => {
    const unsubscribe = onNotification((message: NotificationMessage) => {
      if (message.type !== 'notification') return;
      if (message.notification_type !== type) return;
      const mapped = mapSocketNotification(message);
      setData(prev => [mapped, ...prev]);
    });
    return unsubscribe;
  }, [onNotification, type]);

  return { data, isLoading, error };
}

export function useMarkAsRead() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const bump = useNotificationsInvalidationStore((s) => s.bump);

  const mutate = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await notificationsApi.markAsRead(id);
      bump();
      setIsLoading(false);
      return result;
    } catch (err) {
      setError(err as Error);
      setIsLoading(false);
      throw err;
    }
  };

  return { mutate, isLoading, error };
}

export function useMarkAllAsRead() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const bump = useNotificationsInvalidationStore((s) => s.bump);

  const mutate = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await notificationsApi.markAllAsRead();
      bump();
      setIsLoading(false);
    } catch (err) {
      setError(err as Error);
      setIsLoading(false);
      throw err;
    }
  };

  return { mutate, isLoading, error };
}

export function useDeleteNotification() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const bump = useNotificationsInvalidationStore((s) => s.bump);

  const mutate = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await notificationsApi.delete(id);
      bump();
      setIsLoading(false);
    } catch (err) {
      setError(err as Error);
      setIsLoading(false);
      throw err;
    }
  };

  return { mutate, isLoading, error };
}

export function useClearAllNotifications() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const bump = useNotificationsInvalidationStore((s) => s.bump);

  const mutate = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await notificationsApi.clearAll();
      bump();
      setIsLoading(false);
    } catch (err) {
      setError(err as Error);
      setIsLoading(false);
      throw err;
    }
  };

  return { mutate, isLoading, error };
}

function mapSocketNotification(message: Extract<NotificationMessage, { type: 'notification' }>): Notification {
  const createdAtMs = (message.created_at ?? Date.now()) * 1000;
  return {
    id: message.id ?? `ws-${Date.now()}`,
    userId: message.user_id ?? '',
    title: message.title ?? '',
    body: message.body ?? message.message ?? '',
    type: (message.notification_type as NotificationType) ?? 'system',
    referenceType: message.reference_type ?? null,
    referenceId: message.reference_id ?? null,
    actionUrl: message.action_url ?? null,
    isRead: message.is_read ?? false,
    readAt: null,
    channels: {
      inApp: true,
      email: false,
      sms: false,
      push: false,
    },
    sentVia: {
      inApp: true,
      email: false,
      sms: false,
      push: false,
    },
    createdAt: new Date(createdAtMs).toISOString(),
  };
}
